import * as React from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";

import { SessionWithRelations, TrainerWithRelations } from "@volley/data";
import { CoachReadyState, CoachStatus } from "@volley/shared/coach-models";

import logger from "../../../log";
import theme from "../../../theme";
import { fetchApi } from "../../../util";
import { FetchError } from "../../../util/fetchApi";
import {
    retry,
    cancelRetry,
    registerRetry,
    isRetryCancelled,
} from "../../../util/retry";
import CloseableDialogTitle from "../../common/CloseableDialogTitle";
import FourDigitInput from "../../common/FourDigitInput";
import SupportLink from "../../common/SupportLink";
import { useCurrentUser } from "../../hooks/currentUser";
import { usePairingContext } from "../../hooks/pairingStatus";

import PairPromoCodeEntry from "./PairPromoCodeEntry";
import PairWaiver from "./PairWaiver";

enum ErrorDialogType {
    GENERIC = "GENERIC",
    TRAINER_BUSY = "TRAINER_BUSY",
    MAINTENANCE = "MAINTENANCE",
    OUT_OF_ORDER = "OUT_OF_ORDER",
    SHUTDOWN = "SHUTDOWN",
    UPDATING = "UPDATING",
    NO_SUBSCRIPTION = "NO_SUBSCRIPTION",
    UNKNOWN = "UNKNOWN",
    INVALID_REFERENCE_NUMBER = "INVALID_REFERENCE_NUMBER",
}

const RETRY_KEY = "pairselect";

const PAIRING_FAILURE = "Connection Failed";

function ErrorDialogMessage({
    type,
}: {
    type: ErrorDialogType;
}): React.JSX.Element {
    switch (type) {
        case ErrorDialogType.SHUTDOWN:
            return (
                <Box component="div">
                    <Typography component="ol" align="left">
                        <li>
                            Hold red Stop button for 15 seconds until trainer
                            lights in the back turn off
                        </li>
                        <li>Turn trainer back on and connect</li>
                    </Typography>
                    <Divider sx={{ m: 2 }} />
                    <Typography sx={{ mx: 2, mb: 2 }}>
                        If you are able, wait until trainer shuts down and the
                        lights go off, then turn back on and connect. This will
                        take up to 2 minutes.
                    </Typography>
                </Box>
            );
        case ErrorDialogType.GENERIC:
            return (
                <>
                    <Typography variant="body2" gutterBottom>
                        Do not connect until the billboard says <em>Volley</em>.
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        If you are still unable to connect, confirm the
                        following:
                    </Typography>
                    <List
                        component="ol"
                        sx={{
                            listStyle: "outside",
                            listStyleType: "decimal",
                            px: 2,
                        }}
                    >
                        <ListItem sx={{ display: "list-item", pl: 0.5 }}>
                            <Typography variant="body2">
                                The batteries are inserted and the trainer is
                                turned on (green button at back of trainer)
                            </Typography>
                        </ListItem>
                        <ListItem sx={{ display: "list-item", pl: 0.5 }}>
                            <Typography variant="body2">
                                The LED(s) are on at back of the trainer
                            </Typography>
                        </ListItem>
                        <ListItem sx={{ display: "list-item", pl: 0.5 }}>
                            <Typography variant="body2">
                                Is the four digit connect code correct?
                            </Typography>
                        </ListItem>
                        <ListItem sx={{ display: "list-item", pl: 0.5 }}>
                            <Typography variant="body2">
                                If the trainer says “No Network” on the
                                billboard, contact support. You are likely
                                experiencing network connection issues.
                            </Typography>
                        </ListItem>
                    </List>
                    <SupportLink>Message Support</SupportLink>
                </>
            );
        case ErrorDialogType.OUT_OF_ORDER:
            return (
                <Typography textAlign="center" gutterBottom mb={2}>
                    Sorry, this trainer is currently out of order.
                </Typography>
            );
        case ErrorDialogType.UPDATING:
            return (
                <>
                    <Typography textAlign="center" gutterBottom mb={4}>
                        The trainer is currently updating. Please wait a moment
                        and try again.
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" my={2}>
                        Do not remove the batteries during this time or shut
                        down the trainer. This could permanently damage the
                        trainer.
                    </Typography>
                </>
            );
        case ErrorDialogType.TRAINER_BUSY:
            return (
                <>
                    <Typography
                        variant="body2"
                        textAlign="center"
                        gutterBottom
                        sx={{ mb: 2 }}
                    >
                        The trainer is still finishing the previous session.
                        Wait a few seconds and connect again.
                    </Typography>
                    <Typography variant="body2" textAlign="center" gutterBottom>
                        If it keeps saying this, the trainer needs attention.
                    </Typography>
                    <SupportLink>Message Support</SupportLink>
                </>
            );
        case ErrorDialogType.MAINTENANCE:
            return (
                <Typography
                    variant="body2"
                    textAlign="center"
                    gutterBottom
                    sx={{ mb: 2 }}
                >
                    The trainer is currently in maintenance mode and being
                    updated. Please leave the trainer powered on, and return it
                    to the storage area. Thank you.
                </Typography>
            );
        case ErrorDialogType.INVALID_REFERENCE_NUMBER:
            return (
                <Typography textAlign="center">
                    The 4 digit code you entered is invalid. Please check the
                    code and try again.
                </Typography>
            );
        case ErrorDialogType.NO_SUBSCRIPTION:
            return (
                <Typography align="center">
                    This location requires an access code to use the trainer.
                    <br />
                    Please enter the code below.
                </Typography>
            );
        default:
            return (
                <Typography
                    variant="body2"
                    textAlign="center"
                    gutterBottom
                    sx={{ mb: 2 }}
                >
                    Unknown error while pairing to trainer
                </Typography>
            );
    }
}

const isUnauthorized = (e: unknown) =>
    (e as FetchError)?.errorCode === "PAIRING_NO_SUBSCRIPTION";
const isOutOfOrder = (e: unknown) =>
    (e as FetchError)?.errorCode === "PAIRING_OUT_OF_ORDER";
const isNotConnectedError = (e: unknown) =>
    (e as FetchError)?.errorCode === "PAIRING_NOT_CONNECTED";
const isInvalidReferenceNumberError = (e: unknown) =>
    (e as FetchError)?.errorCode === "PAIRING_INVALID_REFERENCE_NUMBER";
const isPairingUnavailableError = (e: unknown): boolean =>
    (e as FetchError)?.errorCode === "PAIRING_UNAVAILABLE";

/**
 * Why the trainer refused to pair.
 *
 * The server has sent both a structured `reason` and the "because: X" message
 * suffix since they were added together (#2518), so neither is a version-skew
 * fallback for the other. What is new is this app's ability to read the
 * structured one at all -- FetchError only started carrying the parsed body
 * recently -- so the field is preferred and the suffix kept as a cheap guard
 * against a response that arrives without a parsed body.
 */
function pairingUnavailableReason(e: unknown): string | null {
    const body = (e as FetchError)?.body as { reason?: unknown } | null;
    if (typeof body?.reason === "string") {
        return body.reason;
    }

    const match = /because: ([A-Z_]+)/.exec((e as Error)?.message ?? "");
    return match ? match[1] : null;
}

/**
 * Reasons the trainer clears on its own within seconds, so the user just needs
 * to wait -- nothing is wrong with the trainer.
 *
 * The reason is the coach ready state (webby-server `routes/sessions.ts` passes
 * through what `pairable.ts` returns), and IDLE/INIT/READY are the *pairable*
 * states, so they never arrive here. DEINIT -- the trainer winding down the
 * previous session -- is the only transient refusal coach can send.
 *
 * The ready state BUSY is deliberately absent and is not the same thing as
 * ErrorDialogType.TRAINER_BUSY: BUSY has no reducer entry in coach and nothing
 * transitions into it, so it cannot arrive as a refusal.
 */
const BUSY_REASONS: readonly string[] = ["DEINIT"] satisfies CoachReadyState[];

/**
 * Whether a refusal is one the trainer will clear on its own. These are worth
 * waiting out: the pairing retry loop usually outlives them, so the user gets a
 * session instead of a dialog telling them to try again.
 */
const isTransientPairingFailure = (e: unknown): boolean => {
    const reason = pairingUnavailableReason(e);
    return reason !== null && BUSY_REASONS.includes(reason);
};
/**
 * EXITING_MAINTENANCE is as unreachable as BUSY today, and is listed only so
 * that wiring it up in coach does not silently fall through to the generic
 * dialog.
 */
const MAINTENANCE_REASONS: readonly string[] = [
    "MAINTENANCE",
    "ENTERING_MAINTENANCE",
    "EXITING_MAINTENANCE",
] satisfies CoachReadyState[];

interface Props {
    alwaysAutoCalibrate?: boolean;
}

export default function PairSelect({
    alwaysAutoCalibrate = false,
}: Props): React.JSX.Element {
    const { autoCalibrate, pairingVerified } = usePairingContext();
    const { currentUser, setCurrentUser } = useCurrentUser();
    const [referenceNumber, setReferenceNumber] = React.useState("####");
    const referenceNumberValid = referenceNumber.indexOf("#") === -1;
    const [waiverOpen, setWaiverOpen] = React.useState(false);
    const [loadingOpen, setLoadingOpen] = React.useState(false);
    const [errorOpen, setErrorOpen] = React.useState(false);
    const [errorType, setErrorType] = React.useState(ErrorDialogType.GENERIC);
    const [errorHeader, setErrorHeader] = React.useState(PAIRING_FAILURE);

    React.useEffect(() => {
        logger.info("Resetting retry status on mount.");
        registerRetry(RETRY_KEY);
        return () => {
            logger.info("Cancelling retry on unmount...");
            cancelRetry(RETRY_KEY);
        };
    }, []);

    const goToTrainer = React.useCallback(
        async (session: SessionWithRelations) => {
            const trainer = await fetchApi<TrainerWithRelations>(
                `/api/trainers/${session.sessionTrainers[0].trainerId}`,
            );
            try {
                await retry(
                    async () => {
                        const status = await fetchApi<CoachStatus>(
                            `/trainer/${trainer.clientId}/api/status?forceRefresh=true`,
                        );
                        if (status?.session?.id !== session.id) {
                            throw new Error("Session on trainer doesn't match");
                        }
                    },
                    RETRY_KEY,
                    1000,
                    5,
                );
                pairingVerified(trainer.clientId, session.id);
            } catch (err: unknown) {
                setLoadingOpen(false);
                // The user closed the dialog; they are not waiting for news.
                if (isRetryCancelled(err)) {
                    return;
                }
                setErrorType(ErrorDialogType.GENERIC);
                setErrorHeader(PAIRING_FAILURE);
                setErrorOpen(true);
            }
        },
        [pairingVerified],
    );

    const pair = React.useCallback(async () => {
        // Closing the loading dialog cancels the retry key, and the cancel flag
        // is sticky -- only registering clears it. Without this, one close
        // leaves every later Connect press rejecting before it reaches the
        // network, silently, for as long as this component stays mounted.
        registerRetry(RETRY_KEY);
        setLoadingOpen(true);
        try {
            const session = await retry(
                async () => {
                    const result = await fetchApi<SessionWithRelations>(
                        "/api/sessions/pair",
                        "POST",
                        {
                            referenceNumber,
                            autoCalibrate:
                                typeof alwaysAutoCalibrate !== "undefined" &&
                                alwaysAutoCalibrate
                                    ? true
                                    : autoCalibrate,
                        },
                    );
                    return result;
                },
                RETRY_KEY,
                1000,
                15,
                (e: unknown) =>
                    isInvalidReferenceNumberError(e) ||
                    (isPairingUnavailableError(e) &&
                        !isTransientPairingFailure(e)) ||
                    isOutOfOrder(e) ||
                    isUnauthorized(e),
            );
            return session;
            // A hook in the session provider makes the user go into the trainer context when there's a session
        } catch (err: unknown) {
            setLoadingOpen(false);
            if (isRetryCancelled(err)) {
                logger.info("Pairing cancelled by the user.");
                return null;
            }

            if (isOutOfOrder(err)) {
                setErrorHeader("Out of Order");
                setErrorType(ErrorDialogType.OUT_OF_ORDER);
                setErrorOpen(true);
                logger.warn("User tried to pair with out of order trainer.");
                return null;
            }

            if (isUnauthorized(err)) {
                const fetchError = err as FetchError;
                if (fetchError.errorCode === "PAIRING_NO_SUBSCRIPTION") {
                    setErrorHeader("Access Code Needed");
                    setErrorType(ErrorDialogType.NO_SUBSCRIPTION);
                } else {
                    setErrorHeader("Unauthorized");
                    setErrorType(ErrorDialogType.UNKNOWN);
                }
                setErrorOpen(true);
                logger.warn(
                    `User tried to pair with unauthorized trainer: ${fetchError.errorCode ?? "?"}`,
                );
                return null;
            }

            if (isPairingUnavailableError(err)) {
                const reason = pairingUnavailableReason(err);
                if (reason === "BOOT") {
                    // Boot error will help user check their battery, but it's also a catch all for
                    // many other problems. Further error message refinement would be needed later on
                    // throughout the various services such as motion and coach.
                    setErrorHeader("Check Batteries & Hardware");
                    setErrorType(ErrorDialogType.GENERIC);
                } else if (reason === "SHUTDOWN") {
                    setErrorHeader("Trainer in Shutdown Process");
                    setErrorType(ErrorDialogType.SHUTDOWN);
                } else if (reason === "UPDATING") {
                    setErrorHeader("Trainer is Updating");
                    setErrorType(ErrorDialogType.UPDATING);
                } else if (reason !== null && BUSY_REASONS.includes(reason)) {
                    setErrorHeader("Trainer Busy");
                    setErrorType(ErrorDialogType.TRAINER_BUSY);
                } else if (
                    reason !== null &&
                    MAINTENANCE_REASONS.includes(reason)
                ) {
                    setErrorHeader(PAIRING_FAILURE);
                    setErrorType(ErrorDialogType.MAINTENANCE);
                } else {
                    // An unrecognised reason is not evidence of maintenance --
                    // telling the user to put a working trainer away is worse
                    // than admitting we do not know why pairing failed.
                    setErrorHeader(PAIRING_FAILURE);
                    setErrorType(ErrorDialogType.GENERIC);
                }
                setErrorOpen(true);
                logger.warn(
                    `User tried to pair while the trainer was unavailable: ${reason ?? "no reason given"}`,
                );
                return null;
            }

            if (isNotConnectedError(err)) {
                setErrorHeader("Trainer Not Connected");
                setErrorType(ErrorDialogType.GENERIC);
                setErrorOpen(true);
                logger.warn(
                    "User tried to pair before trainer was connected to bridge.",
                );
                return null;
            }

            if (isInvalidReferenceNumberError(err)) {
                setErrorHeader("Invalid Code");
                setErrorType(ErrorDialogType.INVALID_REFERENCE_NUMBER);
                setErrorOpen(true);
                logger.warn(
                    `User tried to pair with an invalid reference number: ${referenceNumber}`,
                );
                return null;
            }

            logger.warn("Unable to pair with trainer after 15 tries.", {
                error: (err as Error).message,
            });
            setErrorType(ErrorDialogType.GENERIC);
            setErrorHeader(PAIRING_FAILURE);
            setErrorOpen(true);
            return null;
        }
    }, [referenceNumber, autoCalibrate, alwaysAutoCalibrate]);

    const onSubmit = React.useCallback(
        async (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            if (currentUser && !currentUser.termsOfServiceAcceptedAt) {
                setWaiverOpen(true);
                return;
            }

            const session = await pair();
            if (session) {
                await goToTrainer(session);
            }
        },
        [pair, currentUser, goToTrainer],
    );

    const onWaiverClose = React.useCallback(
        async (accepted: boolean) => {
            setWaiverOpen(false);
            if (accepted && currentUser) {
                setCurrentUser({
                    ...currentUser,
                    termsOfServiceAcceptedAt: new Date(),
                });
                const session = await pair();
                if (session) {
                    await goToTrainer(session);
                }
            }
        },
        [currentUser, setCurrentUser, pair, goToTrainer],
    );

    const onPromoCodeEntryDone = React.useCallback(async () => {
        setErrorOpen(false);
        const session = await pair();
        if (session) await goToTrainer(session);
    }, [pair, goToTrainer]);

    const onLoadingClosed = React.useCallback(() => {
        logger.info("Cancelling retry on dialog close");
        setLoadingOpen(false);
        cancelRetry(RETRY_KEY);
    }, []);

    return (
        <Container maxWidth="xs" component="form" onSubmit={onSubmit}>
            <Typography
                variant="h3"
                component="h2"
                sx={{ mt: 2, fontSize: "17pt" }}
                gutterBottom
                color="primary.main"
            >
                Enter Trainer ID to Begin
            </Typography>
            <Divider sx={{ my: 2, borderColor: theme.palette.primary.main }} />
            <List
                component="ol"
                sx={{ listStyle: "outside", listStyleType: "decimal", px: 2 }}
            >
                <ListItem sx={{ display: "list-item", pl: 0.5 }}>
                    <ListItemText>
                        If the trainer is off, power on by pressing the green
                        button, located at the rear of the trainer.
                    </ListItemText>
                </ListItem>
                <ListItem sx={{ display: "list-item", pl: 0.5 }}>
                    <ListItemText>
                        Enter the 4 digit number located on the side of the
                        trainer.
                    </ListItemText>
                </ListItem>
            </List>
            <FourDigitInput
                value={referenceNumber}
                onChange={setReferenceNumber}
            />
            <Box component="div" sx={{ my: 2 }}>
                <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    color="secondary"
                    disabled={!referenceNumberValid}
                >
                    Connect
                </Button>
            </Box>

            {/* Waiver Dialog */}
            <PairWaiver open={waiverOpen} onClose={onWaiverClose} />

            {/* Loading Dialog */}
            <Dialog open={loadingOpen} onClose={() => onLoadingClosed()}>
                <CloseableDialogTitle onClose={() => onLoadingClosed()}>
                    <Typography variant="h4" component="p" color="primary">
                        Connecting to the Trainer
                    </Typography>
                </CloseableDialogTitle>
                <Divider sx={{ mx: 2 }} />
                <DialogContent>
                    <Typography align="center" variant="body2" gutterBottom>
                        Please wait a moment while we connect with the trainer.
                    </Typography>
                    <Box
                        component="div"
                        display="flex"
                        justifyContent="center"
                        my={2}
                    >
                        <CircularProgress
                            variant="indeterminate"
                            color="info"
                            size={60}
                        />
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Error Dialog */}
            <Dialog open={errorOpen} onClose={() => setErrorOpen(false)}>
                <CloseableDialogTitle onClose={() => setErrorOpen(false)}>
                    <Typography variant="h4" component="p" color="primary">
                        {errorHeader}
                    </Typography>
                </CloseableDialogTitle>
                <Divider sx={{ mx: 2 }} />
                <DialogContent>
                    <ErrorDialogMessage type={errorType} />
                    {errorType === ErrorDialogType.NO_SUBSCRIPTION ? (
                        <PairPromoCodeEntry onDone={onPromoCodeEntryDone} />
                    ) : (
                        <Button
                            type="button"
                            variant="contained"
                            size="large"
                            fullWidth
                            color="secondary"
                            onClick={() => setErrorOpen(false)}
                            sx={{ mt: 1 }}
                        >
                            OK
                        </Button>
                    )}
                </DialogContent>
            </Dialog>
        </Container>
    );
}
